/**
 * Global Filters Slice
 * Redux slice for global filters like outlet selection, search, and notifications
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: string;
}

export interface GlobalFiltersState {
  selectedOutletId: string | null;
  globalSearch: string;
  notificationCount: number;
  notifications: AppNotification[];
}

const initialState: GlobalFiltersState = {
  selectedOutletId: null,
  globalSearch: '',
  notificationCount: 0,
  notifications: [],
};

const globalFiltersSlice = createSlice({
  name: 'globalFilters',
  initialState,
  reducers: {
    setSelectedOutletId: (state, action: PayloadAction<string | null>) => {
      state.selectedOutletId = action.payload;
    },
    setGlobalSearch: (state, action: PayloadAction<string>) => {
      state.globalSearch = action.payload;
    },
    setNotifications: (state, action: PayloadAction<AppNotification[]>) => {
      state.notifications = action.payload;
      state.notificationCount = action.payload.filter((n) => !n.read).length;
    },
    markNotificationRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(
        (n) => n.id === action.payload,
      );
      if (notification) {
        notification.read = true;
        state.notificationCount = state.notifications.filter(
          (n) => !n.read,
        ).length;
      }
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach((n) => {
        n.read = true;
      });
      state.notificationCount = 0;
    },
    clearGlobalFilters: () => initialState,
  },
});

export const {
  setSelectedOutletId,
  setGlobalSearch,
  setNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearGlobalFilters,
} = globalFiltersSlice.actions;

export default globalFiltersSlice.reducer;
